// FacturaPDF.tsx
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import html2pdf from 'html2pdf.js';
import { ContratoServiciosProps } from '@/types';
import { EstilosPdfCss } from '../../../styles';
import { blobToBase64, defaultPdfOptions } from '../../../utils/pdfHelpers';

// Interfaz del ref: ahora los métodos aceptan opcionalmente el número de factura
export interface FacturaPdfRef {
  generarPdfComoBase64: (numeroFactura?: string) => Promise<string | null>;
  generarPdfFactura: (numeroFactura?: string) => Promise<string | null>;
}

const FacturaPDF = forwardRef<FacturaPdfRef, ContratoServiciosProps & {
  transactionId: string;
  numeroFactura?: string;
}>(({
  selectedProfesiones,
  formData1,
  paymentMethod,
  currentDate,
  isEmpresa,
  precioOriginal,
  descuento,
  numeroFactura,
}, ref) => {

  const facturaRef = useRef<HTMLDivElement>(null);

  const [numFactura, setNumFactura] = useState<string>(numeroFactura ?? "");
  const [totalPagar, setTotalPagar] = useState<number>(precioOriginal ?? 0);
  const [montoIVA, setMontoIVA] = useState<number>(0);
  const [iva] = useState<number>(21);
  const [precioConDescuento, setPrecioConDescuento] = useState<number>(precioOriginal ?? 0);
  const [descuentoEnEuros, setDescuentoEnEuros] = useState<number>(0);

  // recalcula importes cuando cambian props relevantes
  useEffect(() => {
    const original = precioOriginal ?? 0;
    const descuentoCalculado = (descuento ?? 0) > 0 ? original * ((descuento ?? 0) / 100) : 0;
    const precioFinalSinIVA = original - descuentoCalculado;
    const calculoIVA = precioFinalSinIVA * (iva / 100);
    const total = precioFinalSinIVA + calculoIVA;

    setPrecioConDescuento(precioFinalSinIVA);
    setDescuentoEnEuros(descuentoCalculado);
    setMontoIVA(calculoIVA);
    setTotalPagar(total);
  }, [precioOriginal, iva, descuento]);

  // sincroniza cuando cambian las props externas
  useEffect(() => {
    setNumFactura(numeroFactura ?? '');
  }, [numeroFactura]);

  // Generador de PDF: acepta opcionalmente numeroFactura para asegurar que aparece en el PDF
  const generarPdfFactura = async (numFacturaParam?: string): Promise<string | null> => {
    if (!facturaRef.current) return null;

    // determinar valor a mostrar (param > estado > placeholder)
    const displayFactura = numFacturaParam ?? numFactura ?? 'Cargando...';

    // actualizamos estado por consistencia
    setNumFactura(displayFactura);

    // escribimos directamente en el DOM para que html2pdf lo capture inmediatamente
    const root = facturaRef.current;
    const spanFactura = root.querySelector('[data-num-factura]') as HTMLElement | null;
    if (spanFactura) spanFactura.textContent = displayFactura;

    // esperar un frame para asegurar repaint antes de generar el blob
    await new Promise(requestAnimationFrame);

    try {
      const pdfBlob = await html2pdf().from(root).set(defaultPdfOptions).outputPdf('blob');
      return await blobToBase64(pdfBlob);
    } catch (error) {
      console.error('Error generando PDF de factura:', error);
      return null;
    }
  };

  // alias igual que antes
  const generarPdfComoBase64 = generarPdfFactura;

  useImperativeHandle(ref, () => ({ generarPdfFactura, generarPdfComoBase64 }), [numFactura, precioOriginal, descuento]);

  const profesionesStr = Array.isArray(selectedProfesiones)
    ? selectedProfesiones.join(', ')
    : selectedProfesiones || '';

  return (
    <div
      ref={facturaRef}
      className={`${EstilosPdfCss.listaPdf} p-4 bg-white text-md text-black`}
    >
      <div className="flex items-center justify-between mb-6">
        <img
          src="/img/logo/LogoLinkServs.png"
          alt="Logo"
          className='text-start w-24 h-20'
        />
        <h1 className="text-2xl font-bold text-center mb-6">FACTURA</h1>
      </div>

      <p><strong>Fecha:</strong> {currentDate}</p>
      {/* span con data-num-factura para manipular desde JS antes de generar el PDF */}
      <p>Factura N°: <span data-num-factura>{numFactura || "Cargando..."}</span></p>

      <div className="flex justify-between my-6">
        <div className="w-1/2 pr-4">
          <h2 className="text-lg font-semibold mb-2">Datos de la Empresa:</h2>
          <p><strong>Nombre:</strong> Cuadranex S.L.</p>
          <p><strong>CIF:</strong> B-26925339</p>
          <p><strong>Dirección:</strong> Calle Oropesa 11, Yuncos (Toledo)</p>
          <p><strong>Teléfono:</strong> +34 (925 004 105 - 671 562 558)</p>
          <p><strong>Email:</strong> contacto@linkservs.com</p>
        </div>

        <div className="w-1/2 pl-4">
          <h2 className="text-lg font-semibold mb-2">Datos del Cliente:</h2>

          {isEmpresa ? (
            <>
              <p><strong>Empresa:</strong> {formData1?.nombre_empresa}</p>
              <p><strong>CIF:</strong> {formData1?.cif}</p>
            </>
          ) : (
            <>
              <p><strong>Nombre:</strong> {formData1?.nombre} {formData1?.apellidos}</p>
              <p><strong>DNI/NIE:</strong> {formData1?.dniNie}</p>
            </>
          )}

          <p><strong>Email:</strong> {formData1?.email}</p>
          <p><strong>Teléfono:</strong> {formData1?.numero_telefono}</p>
          <p><strong>Dirección:</strong> {formData1?.calle} {formData1?.locality}, {formData1?.province}, {formData1?.postalCode}</p>
        </div>
      </div>

      <div className="my-6 border-y-2 pb-6">
        <h2 className="text-lg font-semibold mb-2">Detalle del Servicio</h2>
        <div className="grid grid-cols-[200px_1fr] gap-y-2">
          <p><strong>Servicios contratados:</strong></p>
          <p>{profesionesStr}</p>

          <p><strong>Duración:</strong></p>
          <p>{paymentMethod}</p>

          <p><strong>Método de pago:</strong></p>
          <p>Tarjeta</p>
        </div>
      </div>

      <div className="my-6 pt-4">
        <h2 className="text-lg font-semibold mb-4">Resumen</h2>

        <div className="grid grid-cols-[200px_1fr] gap-y-0">
          {[
            ...(descuento! > 0
              ? [
                ['Precio original:', `${(precioOriginal ?? 0).toFixed(2)} €`],
                ['Descuento aplicado:', `${descuento}%`],
                ['Descuento en euros:', `${descuentoEnEuros.toFixed(2)} €`],
              ]
              : []),

            ['Base imponible:', `${precioConDescuento.toFixed(2)} €`],
            [`IVA (${iva}%):`, `${montoIVA.toFixed(2)} €`],
            ['Total:', `${totalPagar.toFixed(2)} €`, true],
          ].map(([label, value, isTotal], idx) => (
            <React.Fragment key={idx}>
              <p
                className={`flex text-center min-h-[35px] px-2 ${idx % 2 === 0 ? 'bg-gray-200' : ''} ${isTotal ? 'font-bold text-xl' : ''}`}
              >
                <strong>{label}</strong>
              </p>
              <p
                className={`flex text-center justify-end min-h-[35px] px-2 ${idx % 2 === 0 ? 'bg-gray-200' : ''} ${isTotal ? 'font-bold text-xl' : ''}`}
              >
                {value}
              </p>
            </React.Fragment>
          ))}
        </div>
      </div>

      <p className="text-sm text-zinc-600 mt-8">
        Esta factura ha sido generada automáticamente. No requiere firma ni sello.
      </p>
    </div>
  );
});

export default FacturaPDF;
